import * as Cesium from "cesium";
import { cleanGeoJSONForCesium } from "../geojson/CleanGeojson";
import { getUsernameAndUserIdFormat, showToast } from "../Utility";
import Type from "../Types";

export function generateRoute({ viewer, dataSource, virtualDS, markerDS, turningDS, turningArcDS, onDone, onAbort, onFinally, verbose = true, onEvent, userData }) {
  const hasSideLayer = virtualDS || markerDS || turningDS || turningArcDS;

  if (!viewer || !dataSource || !hasSideLayer || typeof onDone !== "function") {
    try {
      showToast("Required inputs are missing (viewer, dataSource, at least one side layer, onDone).", Type.NOTIFICATION.WARNING, { duration: 5000, closable: true });
    } catch {}
    const noop = () => {};
    noop.pause = () => {};
    noop.resume = () => {};
    return noop;
  }

  // Dead-end / abort message helper
  const getDeadEndMessage = (reason, detail = {}) => {
    const id = detail.currentId ?? detail.id;
    const nextId = detail.nextId;
    const suffix = id != null ? ` (${id})` : "";
    const nextSuffix = nextId != null ? ` -> next ${nextId}` : "";

    const messages = {
      "overlapping-candidates": "More than one candidate lanelet shares the same geometry at this junction.",

      "side-not-connected-right": "Right border line is not connected to next right border line.",
      "side-not-connected-left": "Left border line is not connected to next left border line.",
      "dead-end-no-next": `Lanelet${suffix} is not connected to any next lanelet.`,
      "no-next-from-start": `Lanelet${suffix} is not connected to any next lanelet.`,
      "no-next": `Lanelet${suffix} is not connected to next lanelet${nextSuffix}.`,

      "side-not-found-right": `Right lane cannot be found for lanelet${suffix}.`,
      "side-not-found-left": `Left lane cannot be found for lanelet${suffix}.`,

      "side-duplicate-right": `Right lane id is duplicated for lanelet${suffix}.`,
      "side-duplicate-left": `Left lane id is duplicated for lanelet${suffix}.`,

      "dead-end-no-viable-branch": `Route cannot continue from lanelet${suffix} because all connected next lanelets are invalid.`,
      "no-viable-branch-start": `Route cannot start from lanelet${suffix} because all connected next lanelets are invalid.`,
      "missing-side-current": `Lanelet${suffix} is missing one or both border lines (left/right).`,
      "missing-side-first-empty": "Cannot start route because the first lanelet has missing or inconsistent border lines.",

      "resume-no-entities": "Cannot resume route; saved lanelets are no longer in the datasource.",
      stopped: "Route generation was stopped by user.",
    };

    let base = messages[reason] || `Route generation stopped (${reason}).`;

    if (reason === "side-not-found-right" || reason === "side-not-found-left") {
      const side = reason === "side-not-found-right" ? "Right" : "Left";
      const layerName = detail.layer || "virtual lane / lane marker / turning line / turning arc";
      base = `${side} border line is not found in "${layerName}" layer.`;
    }

    if (reason === "side-duplicate-right" || reason === "side-duplicate-left") {
      const side = reason === "side-duplicate-right" ? "Right" : "Left";
      const layerName = detail.layer || "virtual lane / lane marker / turning line";
      const dup = typeof detail.duplicateCount === "number" ? detail.duplicateCount : null;
      const countText = dup && dup > 1 ? ` Found ${dup} candidates with the same id.` : "";
      base = `${side} border line has multiple candidates (duplicated ids) in "${layerName}" layer.${countText}`;
    }

    if ((reason === "side-not-connected-right" || reason === "side-not-connected-left") && typeof detail.message === "string") {
      base = detail.message;
    }

    if (reason === "overlapping-candidates" && Array.isArray(detail.conflicts) && detail.conflicts.length > 0) {
      const lines = detail.conflicts.map((group) => {
        const ids = Array.isArray(group.ids) ? group.ids.join(", ") : "";
        return `Overlapping lanelets: ${ids}`;
      });

      return `${base}\n${lines.join("\n")}`;
    }

    const rej = Array.isArray(detail.rejected) ? detail.rejected : [];
    if (rej.length === 0) return base;

    // map internal reason codes to human-readable English labels
    const reasonLabel = (rCode, d) => {
      switch (rCode) {
        case "no-future": {
          const depth = d && typeof d.depth === "number" ? d.depth : null;
          const base = depth != null ? `No valid continuation within the next ${depth} lanelet(s).` : "No valid continuation from this lanelet.";

          const fr = Array.isArray(d?.futureReasons) ? d.futureReasons : null;
          if (!fr || !fr.length) return base;

          const MAX_FUTURE_ITEMS = 3;

          const parts = fr
            .map((item) => {
              const code = item.reason || item.code;
              if (!code || code === "no-future") return null; // avoid recursion

              const human = reasonLabel(code, item.detail || item);
              if (!human) return null;

              const idPart = item.id != null ? `lanelet ${item.id}: ` : "";
              return `${idPart}${human}`;
            })
            .filter(Boolean);

          if (!parts.length) return base;

          const shown = parts.slice(0, MAX_FUTURE_ITEMS);
          if (parts.length > MAX_FUTURE_ITEMS) {
            shown.push(`... and ${parts.length - MAX_FUTURE_ITEMS} more lanelets`);
          }

          return `${base} Failing condition(s) ahead: ${shown.join(" | ")}`;
        }

        case "dead-end-no-next": {
          const laneId = d?.currentId ?? d?.id;
          if (laneId != null) {
            return `Lanelet ${laneId} is not connected to any next lanelet.`;
          }
          return "Lanelet is not connected to any next lanelet.";
        }

        case "side-not-connected-right":
          return "Right border line is not connected to next right border line.";
        case "side-not-connected-left":
          return "Left border line is not connected to next left border line.";
        case "side-direction-right":
          return "Right side line direction does not match the next right side line.";
        case "side-direction-left":
          return "Left side line direction does not match the next left side line.";
        case "side-not-found-right":
        case "side-not-found-left": {
          const side = rCode === "side-not-found-right" ? "Right" : "Left";
          const layerName = d?.layer || "virtual lane / lane marker / turning line";
          return `${side} border line is not found in "${layerName}" layer.`;
        }
        case "side-duplicate-right":
        case "side-duplicate-left": {
          const side = rCode === "side-duplicate-right" ? "Right" : "Left";
          const layerName = d?.layer || "virtual lane / lane marker / turning line";
          const dup = typeof d?.duplicateCount === "number" ? d.duplicateCount : null;
          const countText = dup && dup > 1 ? ` Found ${dup} candidates with the same id.` : "";
          return `${side} border line has multiple candidates (duplicated ids) in "${layerName}" layer.${countText}`;
        }

        case "missing-side-next": {
          const nextId = d?.nextId;
          const sides = [];

          const explainSide = (name, info) => {
            if (!info) return;
            if (info.exists) return;

            const label = name === "right" ? "Right border line" : "Left border line";
            const idPart = info.id != null ? ` (id: ${info.id})` : "";

            let reasonText = "is missing.";
            if (info.problem === "no-id") {
              reasonText = "is missing because lanelet has no id set.";
            } else if (info.problem === "id-not-found") {
              reasonText = "id is set but no matching line was found in any side layer.";
            } else if (info.problem === "type-mismatch") {
              const layer = info.layer || "unknown layer";
              const t = info.type || "unknown type";
              reasonText = `exists with this id, but its layer (“${layer}”) does not match expected type (“${t}”).`;
            }

            sides.push(`${label}${idPart} ${reasonText}`);
          };

          explainSide("right", d.right);
          explainSide("left", d.left);

          const prefix = nextId != null ? `Next lanelet ${nextId}: ` : "Next lanelet: ";

          if (!sides.length) {
            return "is missing one or both border lines (left/right).";
          }
          return sides.join(" | ");
        }

        case "missing-side-current":
          return "Current lanelet is missing one or both border lines (left/right).";
        case "no-connection":
          return "Lanelet endpoints do not connect.";

        case "visited":
          return "Lanelet was already visited in the route.";
        case "self-loop":
          return "Lanelet would create a loop back to itself.";
        case "no-polyline":
          return "Lanelet has no geometry (polyline).";
        case "angle-too-large": {
          const a = typeof d?.angle === "number" ? d.angle.toFixed(1) : null;
          const th = typeof d?.threshold === "number" ? d.threshold.toFixed(1) : null;
          if (a != null && th != null) {
            return `Turn angle ${a}° is too sharp (limit ${th}°).`;
          }
          return "Turn angle is too sharp at this junction.";
        }

        default:
          return rCode || "unknown";
      }
    };

    const lines = rej.map((r) => {
      const candId = r.id != null ? r.id : "?";
      const rCode = r.reason || "unknown";
      const d = r.detail || {};

      const msg = typeof d === "string" ? d : typeof d?.message === "string" ? d.message : "";

      const dist = typeof d?.distance === "number" ? ` (distance ≈ ${d.distance.toFixed(2)} m)` : "";

      const label = msg || reasonLabel(rCode, d);

      // underscore instead of "1." / "2." / ...
      return `Lanelet ${candId}: ${label}${dist}`;
    });

    return `${base}\n${lines.join("\n")}`;
  };

  const LOOKAHEAD_DEPTH = 2;

  showToast("Click a lanelet to begin", Type.NOTIFICATION.INFO, { duration: 2000, closable: true });

  // ---- perf helpers ----
  // English comment: keep only one active rAF for this run
  let rafId = null;

  const schedule = (fn) => {
    if (finished) return; // do not schedule after finish
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    rafId = requestAnimationFrame(() => {
      rafId = null;
      try {
        fn();
      } catch {}
    });
  };

  // ---- input handler ----
  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  // ---- runtime state ----
  let started = false,
    finished = false,
    succeeded = false,
    paused = false;
  let currentEntity = null,
    currentReversed = false;

  let orderedLanelets = [];
  let reversedFlags = [];
  let rightSides = [];
  let leftSides = [];

  let branchCandidates = [];
  let pickingBranch = false;
  const visitedIds = new Set();
  const decisionPoints = []; // { len, currentId, currentReversed }

  // ---- style memory ----
  const styled = new Map();
  const SELECTED_WIDTH = 6,
    BRANCH_WIDTH = 6,
    SIDE_WIDTH = 5;

  const NEXT_RADIUS_METERS = 50;

  // ---- side DS indexes ----
  const idxVirtual = buildIndex(virtualDS, "virtual_lane");
  const idxMarker = buildIndex(markerDS, "lane_marker");
  const idxTurning = buildIndex(turningDS, "turningline");
  const idxTurningArc = buildIndex(turningArcDS, "turningarc");

  function buildIndex(ds, layerName) {
    const byId = new Map();
    const dupCount = new Map();

    try {
      const vals = ds?.entities?.values || [];
      for (let i = 0; i < vals.length; i++) {
        const e = vals[i];
        const idv = getProp(e, "id");
        if (idv == null) continue;
        const k = String(idv);

        if (!byId.has(k)) {
          byId.set(k, e); // first entity
        } else {
          // simple duplicate counter
          dupCount.set(k, (dupCount.get(k) || 1) + 1);
        }
      }
    } catch {}

    return { byId, dupCount, layerName };
  }

  function findSideById(id) {
    if (id == null) return null;
    const k = String(id);

    const indices = [idxVirtual, idxMarker, idxTurning, idxTurningArc];

    for (let i = 0; i < indices.length; i++) {
      const idx = indices[i];
      const ent = idx.byId.get(k);
      if (!ent) continue;

      const dup = idx.dupCount.get(k) || 1;

      return {
        entity: ent,
        layer: idx.layerName,
        duplicateCount: dup,
      };
    }

    return null;
  }

  // English comment: normalize type / layer strings for comparison
  function normalizeTypeString(str) {
    if (!str || typeof str !== "string") return "";
    return str.toLowerCase().replace(/[\s\-_]+/g, "");
  }

  // English comment: ensure side layerName matches expected type
  function filterSideByType(info, expectedType) {
    if (!info) return null;
    // if lanelet has no explicit type, keep original behavior
    if (!expectedType) return info;

    const layerNorm = normalizeTypeString(info.layer || "");
    const typeNorm = normalizeTypeString(expectedType);

    if (!typeNorm) return info;

    if (layerNorm.includes(typeNorm)) return info;

    // type mismatch: treat as not found
    return null;
  }

  // English comment: cache side entities per lanelet id
  const laneSides = new Map();

  (function buildLaneSides() {
    try {
      const ents = dataSource?.entities?.values || [];
      for (let i = 0; i < ents.length; i++) {
        const e = ents[i];
        if (!e?.polyline) continue;

        const id = String(getId(e));

        const rid = getProp(e, "right_line_id");
        const lid = getProp(e, "left_line_id");

        const rightType = getProp(e, "right_line_type");
        const leftType = getProp(e, "left_line_type");

        const rInfoRaw = findSideById(rid);
        const lInfoRaw = findSideById(lid);

        // English comment: apply type filtering based on lanelet's right/left_line_type
        const rInfo = filterSideByType(rInfoRaw, rightType);
        const lInfo = filterSideByType(lInfoRaw, leftType);

        laneSides.set(id, {
          right: rInfo?.entity || null,
          left: lInfo?.entity || null,
          rightMeta: rInfo || null,
          leftMeta: lInfo || null,
        });
      }
    } catch (e) {}
  })();

  function getSidesFor(entity) {
    const id = String(getId(entity));
    const entry = laneSides.get(id);
    if (!entry) {
      return { right: null, left: null, rightMeta: null, leftMeta: null };
    }
    return {
      right: entry.right || null,
      left: entry.left || null,
      rightMeta: entry.rightMeta || null,
      leftMeta: entry.leftMeta || null,
    };
  }

  // English comment: cache Cartographic for distance / key / lon-lat computations
  const cartoCacheForGeom = new WeakMap();

  function cartoFromCartesian(cart) {
    if (!cart) return null;
    let c = cartoCacheForGeom.get(cart);
    if (!c) {
      c = Cesium.Cartographic.fromCartesian(cart);
      cartoCacheForGeom.set(cart, c);
    }
    return c;
  }

  // ---- spatial index for lanelet endpoints ----
  function metersToDegreesLat(m) {
    return m / 111000;
  }

  function metersToDegreesLon(m, latRad) {
    const cosLat = Math.cos(latRad || 0);
    if (cosLat === 0) return 0;
    return m / (111000 * cosLat);
  }

  function buildLaneletSpatialIndex(entities, radiusMeters) {
    if (!entities || !entities.length) return null;

    const points = [];
    let latSum = 0;
    let latCount = 0;

    for (let i = 0; i < entities.length; i++) {
      const e = entities[i];
      if (!e?.polyline) continue;

      const pos = toPositionsArray(e);
      if (!pos || pos.length < 2) continue;

      const first = pos[0];
      const last = pos[pos.length - 1];

      const cf = cartoFromCartesian(first);
      const cl = cartoFromCartesian(last);

      if (cf) {
        points.push({ cartesian: first, carto: cf, type: "start", entity: e });
        latSum += cf.latitude;
        latCount++;
      }
      if (cl) {
        points.push({ cartesian: last, carto: cl, type: "end", entity: e });
        latSum += cl.latitude;
        latCount++;
      }
    }

    if (!latCount) return null;

    const meanLat = latSum / latCount;
    const cellLat = metersToDegreesLat(radiusMeters);
    const cellLon = metersToDegreesLon(radiusMeters, meanLat);

    const grid = new Map();

    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const col = Math.floor(p.carto.longitude / cellLon);
      const row = Math.floor(p.carto.latitude / cellLat);
      const key = `${col}:${row}`;
      let cell = grid.get(key);
      if (!cell) {
        cell = [];
        grid.set(key, cell);
      }
      cell.push(p);
    }

    return {
      grid,
      cellLat,
      cellLon,
      radiusMeters,
    };
  }

  function queryLaneletSpatialIndex(index, endPoint, oneWay) {
    if (!index || !endPoint) return [];

    const endCarto = cartoFromCartesian(endPoint);
    if (!endCarto) return [];

    const { grid, cellLat, cellLon, radiusMeters } = index;

    const col0 = Math.floor(endCarto.longitude / cellLon);
    const row0 = Math.floor(endCarto.latitude / cellLat);

    const result = [];
    const seen = new Set();

    for (let dc = -1; dc <= 1; dc++) {
      for (let dr = -1; dr <= 1; dr++) {
        const key = `${col0 + dc}:${row0 + dr}`;
        const cell = grid.get(key);
        if (!cell) continue;

        for (let i = 0; i < cell.length; i++) {
          const item = cell[i];
          if (item.type === "end" && oneWay) continue;

          const eid = getId(item.entity);
          if (seen.has(eid)) continue;

          const d = distanceMeters(endPoint, item.cartesian);
          if (d <= radiusMeters) {
            seen.add(eid);
            result.push(item.entity);
          }
        }
      }
    }

    return result;
  }

  function cartToLonLat(cart) {
    const c = cartoFromCartesian(cart);
    if (!c) return null;
    return [Cesium.Math.toDegrees(c.longitude), Cesium.Math.toDegrees(c.latitude)];
  }

  function samePoint(a, b) {
    if (!a || !b) return false;

    // English comment: use cached Cartographic
    const c1 = cartoFromCartesian(a);
    const c2 = cartoFromCartesian(b);
    if (!c1 || !c2) return false;

    const R = Cesium.Ellipsoid.WGS84.maximumRadius;

    const dLon = c1.longitude - c2.longitude;
    const dLat = c1.latitude - c2.latitude;
    const meanLat = 0.5 * (c1.latitude + c2.latitude);

    const x = R * dLon * Math.cos(meanLat);
    const y = R * dLat;
    const dist = Math.sqrt(x * x + y * y);

    const EPS_M = 0.005;
    return dist < EPS_M;
  }
  function distanceMeters(a, b) {
    if (!a || !b) return Number.POSITIVE_INFINITY;

    // use cached Cartographic
    const c1 = cartoFromCartesian(a);
    const c2 = cartoFromCartesian(b);
    if (!c1 || !c2) return Number.POSITIVE_INFINITY;

    const R = Cesium.Ellipsoid.WGS84.maximumRadius;

    const dLon = c1.longitude - c2.longitude;
    const dLat = c1.latitude - c2.latitude;
    const meanLat = 0.5 * (c1.latitude + c2.latitude);

    const x = R * dLon * Math.cos(meanLat);
    const y = R * dLat;
    return Math.sqrt(x * x + y * y);
  }
  function horizontalUnitVector(a, b) {
    if (!a || !b) return null;

    const c1 = cartoFromCartesian(a);
    const c2 = cartoFromCartesian(b);
    if (!c1 || !c2) return null;

    const R = Cesium.Ellipsoid.WGS84.maximumRadius;
    const dLon = c2.longitude - c1.longitude;
    const dLat = c2.latitude - c1.latitude;
    const meanLat = 0.5 * (c1.latitude + c2.latitude);

    const x = R * dLon * Math.cos(meanLat); // east
    const y = R * dLat; // north

    const len = Math.sqrt(x * x + y * y);
    if (len === 0) return null;

    return { x: x / len, y: y / len };
  }

  function incomingDirectionVector(entity, reversed) {
    const pos = toPositionsArray(entity);
    if (!pos || pos.length < 2) return null;
    const n = pos.length;

    let a, b;
    if (!reversed) {
      a = pos[n - 2];
      b = pos[n - 1];
    } else {
      a = pos[1];
      b = pos[0];
    }
    return horizontalUnitVector(a, b);
  }

  function outgoingDirectionVector(entity, reversed) {
    const pos = toPositionsArray(entity);
    if (!pos || pos.length < 2) return null;
    const n = pos.length;

    let a, b;
    if (!reversed) {
      a = pos[0];
      b = pos[1];
    } else {
      a = pos[n - 1];
      b = pos[n - 2];
    }
    return horizontalUnitVector(a, b);
  }

  function toPositionsArray(entity) {
    const pg = entity?.polyline;
    if (!pg) return null;
    const positionsProp = pg.positions;
    if (!positionsProp) return null;
    const positions = positionsProp.getValue(viewer.clock.currentTime);
    if (!positions || positions.length < 2) return null;
    return positions;
  }
  function firstPoint(e) {
    const p = toPositionsArray(e);
    return p ? p[0] : null;
  }
  function lastPoint(e) {
    const p = toPositionsArray(e);
    return p ? p[p.length - 1] : null;
  }
  function lp(e, rev) {
    return rev ? firstPoint(e) : lastPoint(e);
  }
  function polylineMidpointCartesian(entity) {
    const pos = toPositionsArray(entity);
    if (!pos || pos.length < 2) return null;
    let total = 0;
    const segLen = new Array(pos.length - 1);
    for (let i = 1; i < pos.length; i++) {
      const d = Cesium.Cartesian3.distance(pos[i - 1], pos[i]);
      segLen[i - 1] = d;
      total += d;
    }
    const half = total / 2;
    let acc = 0;
    for (let i = 0; i < segLen.length; i++) {
      if (acc + segLen[i] >= half) {
        const t = (half - acc) / segLen[i];
        return Cesium.Cartesian3.lerp(pos[i], pos[i + 1], t, new Cesium.Cartesian3());
      }
      acc += segLen[i];
    }
    return pos[Math.floor(pos.length / 2)];
  }

  function angleBetweenLaneDirections(curEntity, curReversed, nextEntity, nextReversed) {
    const vIn = incomingDirectionVector(curEntity, curReversed);
    const vOut = outgoingDirectionVector(nextEntity, nextReversed);
    if (!vIn || !vOut) return null;

    const dot = vIn.x * vOut.x + vIn.y * vOut.y;
    const clamped = Math.max(-1, Math.min(1, dot));
    const rad = Math.acos(clamped);
    return (rad * 180) / Math.PI;
  }

  // English comment: midpoint between two Cartesian3 points
  function midCartesian(a, b) {
    if (!a || !b) return null;
    return Cesium.Cartesian3.lerp(a, b, 0.5, new Cesium.Cartesian3());
  }
  // ---- props ----
  function getProp(entity, key) {
    const props = entity?.properties;
    const p = props && props[key];
    if (!p) return undefined;
    if (typeof p.getValue === "function") return p.getValue(viewer.clock.currentTime);
    return p;
  }
  function getId(entity) {
    const pid = getProp(entity, "id");
    return pid ?? entity.id ?? String(entity._id ?? Math.random());
  }
  function isOneWay(entity) {
    const v = getProp(entity, "one_way");
    return v !== false;
  }

  // ---- styling ----
  function getWidthNumber(w) {
    const t = viewer.clock.currentTime;
    if (w && typeof w.getValue === "function") return w.getValue(t);
    return typeof w === "number" ? w : 1;
  }
  function setWidth(e, num) {
    const w = e?.polyline?.width;
    if (!w) return;
    if (typeof w.setValue === "function") w.setValue(num);
    else e.polyline.width = num;
  }
  function rememberOriginal(e) {
    if (e?.polyline && !styled.has(e.id)) {
      styled.set(e.id, { material: e.polyline.material, width: getWidthNumber(e.polyline.width) });
    }
  }

  function applyOverlayStyle(e, color, widthNum) {
    if (!e?.polyline) return;

    let entry = styled.get(e.id);
    if (!entry) {
      entry = {};
      styled.set(e.id, entry);
    }

    let overlay = entry.overlay;
    if (overlay && overlay.isDestroyed?.()) {
      overlay = null;
    }

    if (!overlay) {
      overlay = viewer.entities.add({
        polyline: new Cesium.PolylineGraphics({
          positions: e.polyline.positions,
          material: color,
          width: widthNum,
          clampToGround: e.polyline.clampToGround,
        }),
      });
      entry.overlay = overlay;
    } else if (overlay.polyline) {
      overlay.polyline.material = color;
      overlay.polyline.width = widthNum;
    }
  }

  function applyDirectStyle(e, color, widthNum) {
    if (!e?.polyline) return;

    let entry = styled.get(e.id);
    if (!entry) {
      entry = {};
      styled.set(e.id, entry);
    }

    if (entry.material === undefined) {
      entry.material = e.polyline.material;
      entry.width = getWidthNumber(e.polyline.width);
    }

    e.polyline.material = color;
    setWidth(e, widthNum);
  }

  function styleSelected(e) {
    applyOverlayStyle(e, Cesium.Color.GREEN, SELECTED_WIDTH);
  }
  function styleBranch(e) {
    applyDirectStyle(e, Cesium.Color.RED, BRANCH_WIDTH);
  }
  function styleRight(e) {
    applyOverlayStyle(e, Cesium.Color.BLUE, SIDE_WIDTH);
  }
  function styleLeft(e) {
    applyOverlayStyle(e, Cesium.Color.PURPLE, SIDE_WIDTH);
  }

  function restoreStyleFor(e) {
    if (!e) return;
    const entry = styled.get(e.id);
    if (!entry) return;

    if (entry.overlay) {
      try {
        viewer.entities.remove(entry.overlay);
      } catch {}
    }

    if (entry.material !== undefined && e.polyline) {
      e.polyline.material = entry.material;
      setWidth(e, entry.width);
    }

    styled.delete(e.id);
  }

  function restoreAllStyled() {
    try {
      styled.forEach((entry, id) => {
        const e = dataSource?.entities?.getById(id) || virtualDS?.entities?.getById(id) || markerDS?.entities?.getById(id) || turningDS?.entities?.getById(id) || turningArcDS?.entities?.getById(id);

        if (entry.overlay) {
          try {
            viewer.entities.remove(entry.overlay);
          } catch {}
        }

        if (entry.material !== undefined && e?.polyline) {
          e.polyline.material = entry.material;
          setWidth(e, entry.width);
        }
      });
      styled.clear();
    } catch (e) {}
  }

  function panToPoint(point) {
    if (!point) return;
    const cam = viewer.camera;
    const range = cam.positionCartographic.height; // use current zoom as range
    const heading = cam.heading;
    const pitch = cam.pitch;
    // center the target point in view (screen center)
    cam.lookAt(point, new Cesium.HeadingPitchRange(heading, pitch, range)); // keep comments in English
    // detach from local transform while keeping pose
    cam.lookAtTransform(Cesium.Matrix4.IDENTITY);
  }

  function panToJunction(entity, reversed) {
    panToPoint(lp(entity, reversed));
  }

  // ---- connectivity: O(1) via indices ----
  function pointKey(c) {
    if (!c) return "";

    // English comment: use cached Cartographic
    const carto = cartoFromCartesian(c);
    if (!carto) return "";

    const lonDeg = Cesium.Math.toDegrees(carto.longitude);
    const latDeg = Cesium.Math.toDegrees(carto.latitude);

    const SCALE = 1e7; // 10^-7 deg ≈ 0.011 m (~1 cm) in lat
    const qLon = Math.round(lonDeg * SCALE) / SCALE;
    const qLat = Math.round(latDeg * SCALE) / SCALE;

    return `${qLon}|${qLat}`;
  }

  function laneGeometryKey(entity) {
    const pos = toPositionsArray(entity);
    if (!pos || !pos.length) return "";
    const SCALE = 1e7;
    const parts = [];
    for (let i = 0; i < pos.length; i++) {
      const c = cartoFromCartesian(pos[i]);
      if (!c) continue;
      const lonDeg = Cesium.Math.toDegrees(c.longitude);
      const latDeg = Cesium.Math.toDegrees(c.latitude);
      const qLon = Math.round(lonDeg * SCALE) / SCALE;
      const qLat = Math.round(latDeg * SCALE) / SCALE;
      parts.push(`${qLon}|${qLat}`);
    }
    return parts.join(";");
  }

  // English comment: remove overlapping candidates by geometry
  // English comment: detect overlapping candidates by geometry
  function dedupeCandidatesByGeometry(entities) {
    if (!entities || entities.length <= 1) {
      return { unique: entities || [], conflicts: [] };
    }

    const unique = [];
    const seenKeyToFirstId = new Map();
    const conflictsMap = new Map();

    for (let i = 0; i < entities.length; i++) {
      const e = entities[i];
      const key = laneGeometryKey(e);

      if (!key) {
        unique.push(e);
        continue;
      }

      const id = getId(e);

      if (!seenKeyToFirstId.has(key)) {
        seenKeyToFirstId.set(key, id);
        unique.push(e);
      } else {
        const firstId = seenKeyToFirstId.get(key);
        let set = conflictsMap.get(key);
        if (!set) {
          set = new Set();
          set.add(firstId);
          conflictsMap.set(key, set);
        }
        set.add(id);
      }
    }

    const conflicts = [];
    conflictsMap.forEach((set, key) => {
      const ids = Array.from(set);
      if (ids.length > 1) {
        conflicts.push({ key, ids });
      }
    });

    return { unique, conflicts };
  }

  const startIndex = new Map(); // key(firstPoint) -> entities starting here
  const endIndex = new Map(); // key(lastPoint) -> entities ending here

  let laneletSpatialIndex = null; // spatial index for lanelet endpoints

  // build adjacency once
  (function buildAdjacency() {
    try {
      const ents = dataSource?.entities?.values || [];

      // English comment: build spatial index once per generate
      laneletSpatialIndex = buildLaneletSpatialIndex(ents, NEXT_RADIUS_METERS);

      for (let i = 0; i < ents.length; i++) {
        const e = ents[i];
        if (!e?.polyline) continue;
        const f = firstPoint(e);
        const l = lastPoint(e);
        if (f) {
          const kf = pointKey(f);
          if (!startIndex.has(kf)) startIndex.set(kf, []);
          startIndex.get(kf).push(e);
        }
        if (l) {
          const kl = pointKey(l);
          if (!endIndex.has(kl)) endIndex.set(kl, []);
          endIndex.get(kl).push(e);
        }
      }
    } catch (e) {}
  })();

  function connectionReversed(curEnd, next, laneOneWay = true) {
    if (samePoint(curEnd, firstPoint(next))) return false;
    if (!laneOneWay && samePoint(curEnd, lastPoint(next))) return true;
    return null;
  }

  const SIDE_SNAP_THRESHOLD_M = 2;
  const MAX_BRANCH_ANGLE_DEG = 70;
  // sideName: 'left' | 'right'
  function checkSideContinuity(curSide, nextSide, laneOneWay = true, sideName = "right") {
    if (!curSide?.polyline || !nextSide?.polyline) {
      return {
        ok: false,
        code: `side-missing-${sideName}`,
        message: `${sideName.toUpperCase()} LINE missing geometry`,
      };
    }

    const cf = firstPoint(curSide),
      cl = lastPoint(curSide);
    const nf = firstPoint(nextSide),
      nl = lastPoint(nextSide);

    if (!cf || !cl || !nf || !nl) {
      return {
        ok: false,
        code: `side-missing-${sideName}`,
        message: `${sideName.toUpperCase()} LINE missing endpoints`,
      };
    }

    const isRight = sideName === "right";
    const sideLabel = isRight ? "Right" : "Left";

    const buildNotConnectedMessage = (dist) => {
      if (dist != null && dist < SIDE_SNAP_THRESHOLD_M) {
        return `${sideLabel} border line is not connected to next ${sideLabel.toLowerCase()} border and needs snapping!`;
      }
      return `${sideLabel} border line is not connected to the next ${sideLabel.toLowerCase()} border and is positioned farther from the expected location.`;
    };

    if (laneOneWay) {
      if (samePoint(cl, nf)) return { ok: true };

      if (samePoint(cl, nl)) {
        return {
          ok: false,
          code: `side-direction-${sideName}`,
          message: `${sideLabel} side line direction does not match the next ${sideLabel.toLowerCase()} side line.`,
        };
      }

      const dist = distanceMeters(cl, nf); // distance in meters

      return {
        ok: false,
        code: `side-not-connected-${sideName}`,
        message: buildNotConnectedMessage(dist),
        distance: dist,
      };
    }

    if (samePoint(cl, nf) || samePoint(cl, nl) || samePoint(cf, nf) || samePoint(cf, nl)) {
      return { ok: true };
    }

    const dist = distanceMeters(cl, nf);

    return {
      ok: false,
      code: `side-not-connected-${sideName}`,
      message: buildNotConnectedMessage(dist),
      distance: dist,
    };
  }

  function resolveAndStyleSidesFor(entity) {
    const { right: r, left: l, rightMeta: rMeta, leftMeta: lMeta } = getSidesFor(entity);
    if (r) styleRight(r);
    if (l) styleLeft(l);

    return { r, l, rMeta, lMeta };
  }

  // next candidates via index
  function nextCandidates(curEntity, curReversed) {
    const curEnd = lp(curEntity, curReversed);
    if (!curEnd) {
      return [];
    }
    const oneWay = isOneWay(curEntity);
    const key = pointKey(curEnd);
    const fwd = startIndex.get(key) || [];
    const revs = oneWay ? [] : endIndex.get(key) || [];
    const out = [];
    const seen = new Set();

    for (let i = 0; i < fwd.length; i++) {
      const e = fwd[i];
      if (!e?.polyline) continue;
      const id = getId(e);
      if (visitedIds.has(id) || seen.has(id)) continue;
      seen.add(id);
      out.push(e);
    }
    if (!oneWay) {
      for (let i = 0; i < revs.length; i++) {
        const e = revs[i];
        if (!e?.polyline) continue;
        const id = getId(e);
        if (visitedIds.has(id) || seen.has(id)) continue;
        seen.add(id);
        out.push(e);
      }
    }

    return out;
  }

  // English comment: compute all viable next states from given lane
  function nextViableStates(fromEntity, fromReversed, fromRight, fromLeft, extraVisited, outRejected) {
    const end = lp(fromEntity, fromReversed);
    if (!end) {
      return [];
    }

    const oneWay = isOneWay(fromEntity);
    let cands;
    if (laneletSpatialIndex) {
      // English comment: use spatial index within NEXT_RADIUS_METERS
      cands = queryLaneletSpatialIndex(laneletSpatialIndex, end, oneWay);
    } else {
      const key = pointKey(end);
      cands = (startIndex.get(key) || []).concat(oneWay ? [] : endIndex.get(key) || []);
    }

    const rejected = [];
    const ok = [];

    const pushRejected = (obj) => {
      rejected.push(obj);
      if (outRejected) outRejected.push(obj);
    };

    for (let i = 0; i < cands.length; i++) {
      const e = cands[i];
      const eid = getId(e);

      if (eid === getId(fromEntity)) {
        pushRejected({ id: eid, reason: "self-loop", detail: null });
        continue;
      }

      if (!e?.polyline) {
        pushRejected({ id: eid, reason: "no-polyline", detail: null });
        continue;
      }

      if (visitedIds.has(eid) || (extraVisited && extraVisited.has(eid))) {
        pushRejected({ id: eid, reason: "visited", detail: null });
        continue;
      }

      const rev = connectionReversed(end, e, oneWay);
      if (rev === null) {
        pushRejected({
          id: eid,
          reason: "no-connection",
          detail: { from: getId(fromEntity), to: eid, oneWay },
        });
        continue;
      }

      const angleDeg = angleBetweenLaneDirections(fromEntity, fromReversed, e, !!rev);
      if (angleDeg != null && angleDeg > MAX_BRANCH_ANGLE_DEG) {
        pushRejected({
          id: eid,
          reason: "angle-too-large",
          detail: { angle: angleDeg, threshold: MAX_BRANCH_ANGLE_DEG },
        });
        continue;
      }

      const { right: r, left: l } = getSidesFor(e);

      if (!r || !l) {
        const rid = getProp(e, "right_line_id");
        const lid = getProp(e, "left_line_id");
        const rightType = getProp(e, "right_line_type");
        const leftType = getProp(e, "left_line_type");

        const detail = {
          nextId: eid,
          right: { exists: !!r, id: rid, type: rightType },
          left: { exists: !!l, id: lid, type: leftType },
        };

        if (!r) {
          if (rid == null) {
            detail.right.problem = "no-id";
          } else {
            const raw = findSideById(rid);
            if (!raw) {
              detail.right.problem = "id-not-found";
            } else if (!filterSideByType(raw, rightType)) {
              detail.right.problem = "type-mismatch";
              detail.right.layer = raw.layer;
            }
          }
        }

        if (!l) {
          if (lid == null) {
            detail.left.problem = "no-id";
          } else {
            const raw = findSideById(lid);
            if (!raw) {
              detail.left.problem = "id-not-found";
            } else if (!filterSideByType(raw, leftType)) {
              detail.left.problem = "type-mismatch";
              detail.left.layer = raw.layer;
            }
          }
        }

        pushRejected({
          id: eid,
          reason: "missing-side-next",
          detail,
        });
        continue;
      }

      if (!fromRight || !fromLeft) {
        pushRejected({
          id: eid,
          reason: "missing-side-current",
          detail: { current: getId(fromEntity), right: !!fromRight, left: !!fromLeft },
        });
        continue;
      }

      const rightCheck = checkSideContinuity(fromRight, r, oneWay, "right");
      if (!rightCheck.ok) {
        pushRejected({
          id: eid,
          reason: rightCheck.code,
          detail: { message: rightCheck.message, distance: rightCheck.distance },
        });
        continue;
      }

      const leftCheck = checkSideContinuity(fromLeft, l, false, "left");
      if (!leftCheck.ok) {
        pushRejected({
          id: eid,
          reason: leftCheck.code,
          detail: { message: leftCheck.message, distance: leftCheck.distance },
        });
        continue;
      }

      ok.push({ entity: e, reversed: !!rev, rightSide: r, leftSide: l });
    }

    return ok;
  }

  // English comment: keep old API for logging (one-step list of entities)
  function validFutureCandidates(fromEntity, fromReversed, fromRight, fromLeft) {
    return nextViableStates(fromEntity, fromReversed, fromRight, fromLeft).map((s) => s.entity);
  }

  function hasFuturePath(fromEntity, fromReversed, fromRight, fromLeft, depth, extraVisited, failureOut) {
    if (depth <= 0) return true;

    const localVisited = extraVisited || new Set();
    const localRejected = [];
    const states = nextViableStates(fromEntity, fromReversed, fromRight, fromLeft, localVisited, localRejected);

    if (states.length === 0) {
      if (failureOut) {
        if (localRejected.length) {
          failureOut.push(...localRejected);
        } else {
          failureOut.push({
            id: getId(fromEntity),
            reason: "dead-end-no-next",
            detail: { currentId: getId(fromEntity) },
          });
        }
      }
      return false;
    }

    for (let i = 0; i < states.length; i++) {
      const s = states[i];
      const eid = getId(s.entity);
      if (localVisited.has(eid)) continue;

      localVisited.add(eid);
      const childFailures = [];
      const ok = hasFuturePath(s.entity, s.reversed, s.rightSide, s.leftSide, depth - 1, localVisited, childFailures);
      localVisited.delete(eid);

      if (ok) return true;
      if (failureOut && childFailures.length) {
        failureOut.push(...childFailures);
      }
    }

    return false;
  }

  // ---- diagnosis helper for logging ----
  function diagnoseCandidate(next) {
    const cur = currentEntity;
    const curRev = currentReversed;
    const laneOneWay = isOneWay(cur);
    const curEnd = lp(cur, curRev);

    const rev = connectionReversed(curEnd, next, laneOneWay);
    if (rev === null) {
      return { ok: false, reason: "no-connection", detail: { current: getId(cur), next: getId(next), oneWay: laneOneWay } };
    }

    const angleDeg = angleBetweenLaneDirections(cur, curRev, next, !!rev);
    if (angleDeg != null && angleDeg > MAX_BRANCH_ANGLE_DEG) {
      return {
        ok: false,
        reason: "angle-too-large",
        detail: {
          angle: angleDeg,
          threshold: MAX_BRANCH_ANGLE_DEG,
        },
      };
    }

    const { right: nextRight, left: nextLeft } = getSidesFor(next);
    if (!nextRight || !nextLeft) {
      return {
        ok: false,
        reason: "missing-side-next",
        detail: { next: getId(next), right: !!nextRight, left: !!nextLeft },
      };
    }

    const curRight = rightSides[rightSides.length - 1];
    const curLeft = leftSides[leftSides.length - 1];
    if (!curRight || !curLeft) {
      return {
        ok: false,
        reason: "missing-side-current",
        detail: { current: getId(cur), right: !!curRight, left: !!curLeft },
      };
    }

    const rightCheck = checkSideContinuity(curRight, nextRight, laneOneWay, "right");
    if (!rightCheck.ok) {
      return {
        ok: false,
        reason: rightCheck.code,
        detail: {
          message: rightCheck.message,
          distance: rightCheck.distance,
        },
      };
    }

    const leftCheck = checkSideContinuity(curLeft, nextLeft, false, "left");
    if (!leftCheck.ok) {
      return {
        ok: false,
        reason: leftCheck.code,
        detail: {
          message: leftCheck.message,
          distance: leftCheck.distance,
        },
      };
    }

    return { ok: true, reason: null, detail: null, rev: !!rev, nextRight, nextLeft };
  }

  // ---- inputs ----
  const keyListener = (ev) => {
    if (finished || paused) return;
    if (ev.key === "Escape" || ev.key === "Esc") stop();
    else if (ev.key === "Enter") doneAndCleanup("user-confirmed", {});
    else if (ev.key === "Backspace") {
      ev.preventDefault();
      stepBack();
    }
  };

  function attachInputs() {
    handler.setInputAction(onLeftClick, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    window.addEventListener("keydown", keyListener, { passive: false });
  }

  function detachInputs() {
    try {
      handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
    } catch {}
    try {
      window.removeEventListener("keydown", keyListener);
    } catch {}
  }

  // ---- click ----
  const onLeftClick = (movement) => {
    if (finished || paused) return;

    const picked = viewer.scene.pick(movement.position);
    if (!picked || !picked.id) return;
    const entity = picked.id;

    if (!dataSource.entities.contains(entity)) return;

    if (!started) {
      if (!entity.polyline) return;

      // init selection state
      const eid = getId(entity);
      started = true;
      currentEntity = entity;
      currentReversed = false;
      orderedLanelets = [entity];
      reversedFlags = [false];
      rightSides = [];
      leftSides = [];

      visitedIds.add(eid);
      styleSelected(entity);
      panToJunction(entity, /*currentReversed=*/ false);

      // resolve sides for first pick
      const { r, l, rMeta, lMeta } = resolveAndStyleSidesFor(entity);

      // detect detailed issues on first lane
      if (!r || !l || (rMeta && rMeta.duplicateCount > 1) || (lMeta && lMeta.duplicateCount > 1)) {
        const curId = getId(entity);

        // prefer more specific messages if possible
        if (!r) {
          const layer = rMeta?.layer || "virtual lane / lane marker / turning line";
          explainAndAbort("side-not-found-right", { currentId: curId, layer });
          return;
        }
        if (!l) {
          const layer = lMeta?.layer || "virtual lane / lane marker / turning line";
          explainAndAbort("side-not-found-left", { currentId: curId, layer });
          return;
        }
        if (rMeta && rMeta.duplicateCount > 1) {
          const layer = rMeta.layer || "virtual lane / lane marker / turning line";
          explainAndAbort("side-duplicate-right", {
            currentId: curId,
            layer,
            duplicateCount: rMeta.duplicateCount,
          });
          return;
        }
        if (lMeta && lMeta.duplicateCount > 1) {
          const layer = lMeta.layer || "virtual lane / lane marker / turning line";
          explainAndAbort("side-duplicate-left", {
            currentId: curId,
            layer,
            duplicateCount: lMeta.duplicateCount,
          });
          return;
        }

        // fallback (should not reach here)
        explainAndAbort("missing-side-first-empty", { currentId: curId });
        return;
      }

      rightSides.push(r);
      leftSides.push(l);

      const cands = nextCandidates(entity, /*curReversed=*/ false);
      if (cands.length === 0) {
        explainAndAbort("no-next-from-start", { currentId: getId(entity) });
        return;
      }

      let viableCount = 0;
      const rejectedStart = [];
      for (let i = 0; i < cands.length; i++) {
        const cand = cands[i];
        const d = diagnoseCandidate(cand);
        if (d.ok) {
          viableCount++;
        } else {
          rejectedStart.push({
            id: getId(cand),
            reason: d.reason,
            detail: d.detail || null,
          });
        }
      }
      if (viableCount === 0) {
        explainAndAbort("no-viable-branch-start", {
          currentId: getId(entity),
          rejected: rejectedStart,
        });
        return;
      }

      // continue normal flow
      schedule(advance);
      return;
    }

    if (pickingBranch) {
      const chosen = branchCandidates.find((e) => e.id === entity.id);
      if (!chosen) return;

      for (const cand of branchCandidates) {
        if (cand.id !== chosen.id) restoreStyleFor(cand);
      }
      branchCandidates = [];
      pickingBranch = false;

      const proceed = tryAdvanceTo(chosen);
      if (!proceed) return;
      schedule(advance);
      return;
    }
  };

  // ---- backspace ----
  function stepBack() {
    if (finished || !started) return;
    if (pickingBranch && decisionPoints.length > 0) decisionPoints.pop();

    if (decisionPoints.length > 0) {
      const snap = decisionPoints.pop();
      const newLen = snap.len;

      orderedLanelets = orderedLanelets.slice(0, newLen);
      reversedFlags = reversedFlags.slice(0, newLen);
      rightSides = rightSides.slice(0, newLen);
      leftSides = leftSides.slice(0, newLen);

      currentEntity = orderedLanelets[newLen - 1];
      currentReversed = reversedFlags[newLen - 1];

      visitedIds.clear();
      for (let i = 0; i < orderedLanelets.length; i++) visitedIds.add(getId(orderedLanelets[i]));

      restoreAllStyled();
      for (let i = 0; i < orderedLanelets.length; i++) {
        styleSelected(orderedLanelets[i]);
        if (rightSides[i]) styleRight(rightSides[i]);
        if (leftSides[i]) styleLeft(leftSides[i]);
      }

      const cands = nextCandidates(currentEntity, currentReversed);
      branchCandidates = cands;
      pickingBranch = true;
      branchCandidates.forEach(styleBranch);

      return;
    }

    restoreAllStyled();
    started = false;
    currentEntity = null;
    currentReversed = false;
    orderedLanelets = [];
    reversedFlags = [];
    rightSides = [];
    leftSides = [];
    branchCandidates = [];
    pickingBranch = false;
    visitedIds.clear();
  }

  // ---- strict advance ----
  function tryAdvanceTo(next) {
    const cur = currentEntity;
    const curRev = currentReversed;
    const curEnd = lp(cur, curRev);
    const laneOneWay = isOneWay(cur);

    const rev = connectionReversed(curEnd, next, laneOneWay);
    if (rev === null) {
      explainAndAbort("no-next", {
        currentId: getId(cur),
        nextId: getId(next),
        note: "Lanelet endpoints do not connect.",
      });
      return false;
    }

    // const angleDeg = angleBetweenLaneDirections(cur, curRev, next, !!rev);
    // if (angleDeg != null && angleDeg > MAX_BRANCH_ANGLE_DEG) {
    //   explainAndAbort("angle-too-large", {
    //     currentId: getId(cur),
    //     nextId: getId(next),
    //     angle: angleDeg,
    //     threshold: MAX_BRANCH_ANGLE_DEG,
    //   });
    //   return false;
    // }

    const { r: nextRight, l: nextLeft, rMeta: nextRightMeta, lMeta: nextLeftMeta } = resolveAndStyleSidesFor(next);

    // detailed issues for side ids on next lane
    if (!nextRight || (nextRightMeta && nextRightMeta.duplicateCount > 1)) {
      const layer = nextRightMeta?.layer || "virtual lane / lane marker / turning line";
      const dup = nextRightMeta?.duplicateCount || 0;
      const reason = !nextRight ? "side-not-found-right" : "side-duplicate-right";

      explainAndAbort(reason, {
        currentId: getId(cur),
        nextId: getId(next),
        layer,
        duplicateCount: dup,
      });
      return false;
    }

    if (!nextLeft || (nextLeftMeta && nextLeftMeta.duplicateCount > 1)) {
      const layer = nextLeftMeta?.layer || "virtual lane / lane marker / turning line";
      const dup = nextLeftMeta?.duplicateCount || 0;
      const reason = !nextLeft ? "side-not-found-left" : "side-duplicate-left";

      explainAndAbort(reason, {
        currentId: getId(cur),
        nextId: getId(next),
        layer,
        duplicateCount: dup,
      });
      return false;
    }

    const curRight = rightSides[rightSides.length - 1];
    const curLeft = leftSides[leftSides.length - 1];
    if (!curRight || !curLeft) {
      explainAndAbort("missing-side-current", {
        currentId: getId(cur),
        rightExists: !!curRight,
        leftExists: !!curLeft,
      });
      return false;
    }

    const rcheck = checkSideContinuity(curRight, nextRight, laneOneWay, "right");
    if (!rcheck.ok) {
      explainAndAbort(rcheck.code, {
        currentId: getId(cur),
        nextId: getId(next),
        message: rcheck.message,
        distance: rcheck.distance,
      });
      return false;
    }

    // left direction not important
    const lcheck = checkSideContinuity(curLeft, nextLeft, false, "left");
    if (!lcheck.ok) {
      explainAndAbort(lcheck.code, {
        currentId: getId(cur),
        nextId: getId(next),
        message: lcheck.message,
        distance: lcheck.distance,
      });
      return false;
    }

    const nid = getId(next);
    visitedIds.add(nid);
    orderedLanelets.push(next);
    reversedFlags.push(!!rev);
    currentEntity = next;
    currentReversed = !!rev;

    rightSides.push(nextRight);
    leftSides.push(nextLeft);

    styleSelected(next);
    panToJunction(currentEntity, currentReversed);
    return true;
  }

  // ---- unified explain + abort ----
  function explainAndAbort(code, detail = {}) {
    const message = getDeadEndMessage(code, detail);

    // Optional UI notification when we decide to stop
    try {
      showToast(message, Type.NOTIFICATION.WARNING, { duration: 30000, closable: true });
    } catch {}

    const extendedDetail = { ...detail, message };

    if (!orderedLanelets.length) {
      // Early stop, no route built
      emitEmptyGeoJSONAndStop(code, extendedDetail);
    } else {
      // Partial route built, but we will not continue
      doneAndCleanup(code, extendedDetail);
    }
  }

  // ---- early empty output helper (shows reason too) ----
  function emitEmptyGeoJSONAndStop(reason = "early-invalid", detail = {}) {
    // optional abort callback for explicit abort semantics
    try {
      if (typeof onAbort === "function") onAbort({ reason, detail, status: "aborted" });
    } catch {}

    try {
      onDone({
        coordinates: [],
        ids: [],
        geojson: { type: "FeatureCollection", features: [] },
        resetStyles: () => {
          try {
            restoreAllStyled();
          } catch {}
        },
        reason,
        status: "aborted",
        detail,
      });
    } catch {}
    finalize("aborted", reason, detail);
  }

  // ---- advance loop (non-blocking) ----
  function advance() {
    if (finished) return;

    let steps = 0;
    const MAX_STEPS_PER_FRAME = 32;

    while (steps < MAX_STEPS_PER_FRAME) {
      steps++;

      const candidates = nextCandidates(currentEntity, currentReversed);

      if (candidates.length === 0) {
        const curId = getId(currentEntity);
        explainAndAbort("dead-end-no-next", { currentId: curId });
        return;
      }

      // branch diagnostics
      const viable = [];
      const rejected = [];
      for (let i = 0; i < candidates.length; i++) {
        const c = candidates[i];
        const diag = diagnoseCandidate(c);
        if (diag.ok) {
          viable.push(c);
        } else {
          rejected.push({
            id: getId(c),
            reason: diag.reason,
            detail: diag.detail || null,
          });
        }
      }

      onEvent &&
        onEvent({
          type: "branch-rejected",
          msg: "Rejected candidates",
          extra: rejected,
          ts: Date.now(),
        });

      if (viable.length === 0) {
        const curId = getId(currentEntity);
        explainAndAbort("dead-end-no-viable-branch", {
          currentId: curId,
          rejected,
        });
        return;
      }

      if (viable.length === 1) {
        const only = viable[0];
        const ok = tryAdvanceTo(only);
        if (!ok) return;
        continue;
      }

      const withFuture = [];
      const noFuture = [];

      for (let i = 0; i < viable.length; i++) {
        const cand = viable[i];

        const { right: nextRight, left: nextLeft } = getSidesFor(cand);
        const laneOneWay = isOneWay(currentEntity);
        const curEnd = lp(currentEntity, currentReversed);
        const rev = connectionReversed(curEnd, cand, laneOneWay);

        const futureReasons = [];
        const hasFuture = hasFuturePath(cand, !!rev, nextRight, nextLeft, LOOKAHEAD_DEPTH, null, futureReasons);

        if (hasFuture) {
          withFuture.push(cand);
        } else {
          noFuture.push({
            id: getId(cand),
            reason: "no-future",
            detail: {
              next: getId(cand),
              depth: LOOKAHEAD_DEPTH,
              futureReasons,
            },
          });
        }
      }

      rejected.push(...noFuture);

      // English comment: detect overlapping candidates by geometry
      const { unique: uniqueWithFuture, conflicts: overlappingGroups } = dedupeCandidatesByGeometry(withFuture);

      if (overlappingGroups.length > 0) {
        explainAndAbort("overlapping-candidates", {
          currentId: getId(currentEntity),
          conflicts: overlappingGroups,
        });
        return;
      }

      if (uniqueWithFuture.length === 0) {
        const curId = getId(currentEntity);
        explainAndAbort("dead-end-no-viable-branch", {
          currentId: curId,
          rejected,
        });
        return;
      }

      if (uniqueWithFuture.length === 1) {
        const only = uniqueWithFuture[0];
        const ok = tryAdvanceTo(only);
        if (!ok) return;
        continue;
      }

      decisionPoints.push({
        len: orderedLanelets.length,
        currentId: getId(currentEntity),
        currentReversed,
      });

      branchCandidates = uniqueWithFuture;
      pickingBranch = true;
      const junction = lp(currentEntity, currentReversed);
      panToPoint(junction);
      branchCandidates.forEach(styleBranch);
      return;
    }

    schedule(advance);
  }

  // English comment: abort safely from doneAndCleanup without recursion
  function abortMissingSideAtFinalize(message) {
    const detail = message ? { note: message } : {};
    emitEmptyGeoJSONAndStop("missing-side-current", detail);
  }

  // ---- success path ----
  function doneAndCleanup(reason, detail = {}) {
    if (finished) return;

    if (!orderedLanelets.length) {
      finalize("aborted", reason || "no-route", detail);
      return;
    }

    const lanelet_ids = orderedLanelets.map(getId);

    // build coordinates from left/right sides
    const n = orderedLanelets.length;
    const L0 = leftSides[0],
      R0 = rightSides[0];
    const Llast = leftSides[n - 1],
      Rlast = rightSides[n - 1];

    if (!L0 || !R0 || !Llast || !Rlast) {
      abortMissingSideAtFinalize("Left/Right sides are required to compute midpoints.");
      return;
    }

    const startCart = midCartesian(firstPoint(L0), firstPoint(R0));
    if (!startCart) {
      abortMissingSideAtFinalize("Cannot compute start midpoint.");
      return;
    }

    const innerCarts = [];
    for (let i = 0; i < n; i++) {
      const Li = leftSides[i],
        Ri = rightSides[i];
      if (!Li || !Ri) {
        abortMissingSideAtFinalize(`Missing sides at index ${i}.`);
        return;
      }
      const midL = polylineMidpointCartesian(Li);
      const midR = polylineMidpointCartesian(Ri);
      const midC = midCartesian(midL, midR);
      if (!midC) {
        abortMissingSideAtFinalize(`Cannot compute center midpoint at index ${i}.`);
        return;
      }
      innerCarts.push(midC);
    }

    const endCart = midCartesian(lastPoint(Llast), lastPoint(Rlast));
    if (!endCart) {
      abortMissingSideAtFinalize("Cannot compute end midpoint.");
      return;
    }

    // ---- route offset (avoid overlap with lanelet centerline) ----
    const OFFSET_MIN_M = 0.2; // 20 cm
    const OFFSET_MAX_M = 0.35; // 35 cm
    const offsetMag = OFFSET_MIN_M + Math.random() * (OFFSET_MAX_M - OFFSET_MIN_M);
    const offsetSigned = (Math.random() < 0.5 ? -1 : 1) * offsetMag; // left/right doesn't matter

    function offsetCartesianByEN(cart, dirEN, offsetM) {
      if (!cart || !dirEN) return cart;

      const c = cartoFromCartesian(cart);
      if (!c) return cart;

      // perpendicular to direction: left normal = (-y, x)
      const nx = -dirEN.y;
      const ny = dirEN.x;

      const R = Cesium.Ellipsoid.WGS84.maximumRadius;
      const dEast = nx * offsetM;
      const dNorth = ny * offsetM;

      const dLat = dNorth / R;
      const cosLat = Math.cos(c.latitude);
      const dLon = cosLat !== 0 ? dEast / (R * cosLat) : 0;

      const out = new Cesium.Cartographic(c.longitude + dLon, c.latitude + dLat, c.height);
      return Cesium.Cartesian3.fromRadians(out.longitude, out.latitude, out.height);
    }

    const cartPoints = [startCart, ...innerCarts, endCart];

    // direction (east/north unit) for each point: start, each inner (lanelet i), end
    const dirs = [];
    dirs.push(outgoingDirectionVector(orderedLanelets[0], reversedFlags[0]) || incomingDirectionVector(orderedLanelets[0], reversedFlags[0]));
    for (let i = 0; i < orderedLanelets.length; i++) {
      dirs.push(outgoingDirectionVector(orderedLanelets[i], reversedFlags[i]) || dirs[0]);
    }
    dirs.push(incomingDirectionVector(orderedLanelets[orderedLanelets.length - 1], reversedFlags[reversedFlags.length - 1]) || dirs[0]);

    const offsetCartPoints = cartPoints.map((p, idx) => offsetCartesianByEN(p, dirs[idx], offsetSigned));
    const coordinates = offsetCartPoints.map(cartToLonLat);

    const geojson = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: { id: 100, lanelet_ids, number_of_lanelets: lanelet_ids.length, creator: getUsernameAndUserIdFormat(userData) },

          geometry: { type: "LineString", coordinates: coordinates.map(([lon, lat]) => [lon, lat]) },
        },
      ],
    };

    succeeded = true;

    const resetStyles = () => {
      try {
        restoreAllStyled();
      } catch {}
    };

    try {
      onDone({
        coordinates,
        ids: lanelet_ids,
        geojson: cleanGeoJSONForCesium(geojson),
        resetStyles,
        reason,
        status: "completed",
        detail,
      });
    } catch (e) {}

    finalize("completed", reason, detail);
  }

  // ---- finalize ----
  function finalize(status, reason, detail = {}) {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    try {
      detachInputs();
    } catch {}
    try {
      if (!handler.isDestroyed?.()) handler.destroy();
    } catch {}
    if (status !== "completed") {
      try {
        restoreAllStyled();
      } catch (e) {}
    }

    const prevSucceeded = succeeded;

    started = false;
    finished = true;
    paused = false;
    currentEntity = null;
    currentReversed = false;
    orderedLanelets = [];
    reversedFlags = [];
    rightSides = [];
    leftSides = [];
    branchCandidates = [];
    pickingBranch = false;
    visitedIds.clear();
    decisionPoints.length = 0;

    // show message BEFORE onFinally

    try {
      onFinally && onFinally({ status, reason, detail });
    } catch {}
  }

  // ---- pause/resume ----
  function rehydrateOnResume() {
    const ids = orderedLanelets.map(getId).filter(Boolean);
    const refreshed = [];
    for (let i = 0; i < ids.length; i++) {
      const e = dataSource.entities.getById(ids[i]);
      if (e?.polyline) refreshed.push(e);
    }
    if (orderedLanelets.length > 0 && refreshed.length === 0) {
      finalize("aborted", "resume-no-entities", {});
      return false;
    }

    orderedLanelets = refreshed;
    visitedIds.clear();
    for (let i = 0; i < orderedLanelets.length; i++) visitedIds.add(getId(orderedLanelets[i]));
    currentEntity = orderedLanelets[orderedLanelets.length - 1] || null;
    currentReversed = reversedFlags[reversedFlags.length - 1] || false;

    restoreAllStyled();
    for (let i = 0; i < orderedLanelets.length; i++) {
      styleSelected(orderedLanelets[i]);
      const { right: r, left: l } = getSidesFor(orderedLanelets[i]);
      rightSides[i] = r || null;
      leftSides[i] = l || null;
      if (r) styleRight(r);
      if (l) styleLeft(l);
    }

    if (currentEntity) {
      const cands = nextCandidates(currentEntity, currentReversed);
      branchCandidates = cands;
      pickingBranch = cands.length > 1;
      if (pickingBranch) {
        branchCandidates.forEach(styleBranch);
      }
    }
    return true;
  }

  function pause() {
    if (finished || paused) return;
    paused = true;
    detachInputs();
  }
  function resume() {
    if (finished || !paused) return;
    const ok = rehydrateOnResume();
    if (!ok) return;
    paused = false;
    attachInputs();
  }

  // ---- external stop ----
  function stop() {
    if (finished) return;
    if (!succeeded) finalize("aborted", "stopped", {});
  }
  stop.pause = pause;
  stop.resume = resume;

  attachInputs();

  return stop;
}
